/**
 * Open purchase orders — the PO half of the purchase ledger.
 *
 * The invoice queue answers "what has arrived to be paid?"; this answers the
 * mirror question the finance team asked for: "what have we ordered that
 * nobody has invoiced yet?" Bucketed by receipt, because the two populations
 * are acted on differently. Goods received with no invoice are chased with the
 * supplier and accrued at month-end; goods not yet received are just waiting.
 *
 * The composed view (`/views/open-purchase-orders`) buckets, orders, pages and
 * flags each order (an invoice looks missing; a purchase invoice is waiting on it); the
 * page holds the bucket and the page number. See
 * docs/features/purchase-invoice-matching/01_product_brief.md, the
 * finance-team feedback decisions (item 3).
 */

import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  OpenOrderBucket,
  OpenPurchaseOrder,
  OpenPurchaseOrderPage,
} from "@shared-core/data/purchase-ledger-types";
import {
  OPEN_ORDER_BUCKETS,
  ageLabel,
  formatDate,
  formatMoney,
} from "@shared-core/domain/purchase-ledger";
import { paths } from "@shared-core/routing/routes";
import { PurchaseLedgerHeader } from "../app/components/domain/purchasing/PurchaseLedgerHeader";
import { ListPagination } from "../app/components/patterns/ListPagination";
import { Alert, AlertDescription } from "../app/components/primitives/alert";
import { Badge } from "../app/components/primitives/badge";
import { Button } from "../app/components/primitives/button";
import { Skeleton } from "../app/components/primitives/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";
import { cn } from "../app/components/primitives/utils";
import { currentTime } from "@shared-core/domain/current-time";

const PAGE_SIZE = 50;

type LoadState =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "loaded"; list: OpenPurchaseOrderPage };

export function PurchaseOrderQueuePage() {
  const adapter = useDataAdapter();
  const [bucket, setBucket] = useState<OpenOrderBucket>("received");
  const [page, setPage] = useState(1);
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    // Longest-waiting first (the view's default): in the received buckets that
    // is the invoice most likely to be missing, so it is the first to chase.
    adapter
      .listOpenPurchaseOrders({ bucket, page, pageSize: PAGE_SIZE })
      .then((list) => {
        if (!cancelled) setState({ kind: "loaded", list });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState({
            kind: "failed",
            message:
              err instanceof Error
                ? err.message
                : "The purchase orders did not load.",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, bucket, page]);

  const list = state.kind === "loaded" ? state.list : null;
  const summary = list
    ? list.looksMissingCount > 0
      ? `${list.looksMissingCount} received ${list.chaseAfterDays}+ days ago with no invoice — worth chasing`
      : `${list.countsByBucket.received + list.countsByBucket.part_received} received with no invoice yet`
    : undefined;
  const now = currentTime();

  return (
    <div className="flex h-full flex-col">
      <PurchaseLedgerHeader summary={summary} />

      <div className="flex flex-wrap gap-1 border-b px-6 pb-3">
        {OPEN_ORDER_BUCKETS.map(({ bucket: key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setBucket(key);
              setPage(1);
            }}
            aria-pressed={bucket === key}
            className={cn(
              "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              bucket === key
                ? "bg-secondary text-secondary-foreground"
                : "text-muted-foreground hover:bg-muted",
            )}
          >
            {label}
            {list && (
              <span className="ml-1.5 tabular-nums opacity-70">
                {list.countsByBucket[key]}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {state.kind === "loading" && (
          <div className="space-y-2 p-6" aria-label="Loading purchase orders">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        )}
        {state.kind === "failed" && (
          <Alert variant="destructive" className="m-6 w-auto">
            <AlertDescription>
              Could not load the purchase orders: {state.message}
            </AlertDescription>
          </Alert>
        )}
        {list &&
          (list.items.length === 0 ? (
            <p className="text-muted-foreground p-6 text-sm">
              No open orders here.
            </p>
          ) : (
            <>
              <Table className="[&_td:first-child]:pl-6 [&_th:first-child]:pl-6 [&_td:last-child]:pr-6 [&_th:last-child]:pr-6">
                <TableHeader>
                  <TableRow>
                    <TableHead>PO</TableHead>
                    <TableHead>Supplier</TableHead>
                    <TableHead>Order</TableHead>
                    <TableHead>Raised</TableHead>
                    <TableHead>
                      {bucket === "not_received" ? "Waiting" : "Received"}
                    </TableHead>
                    <TableHead className="text-right">Ordered</TableHead>
                    <TableHead className="w-[6rem]" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {list.items.map((po) => (
                    <OpenOrderRow key={po.id} po={po} now={now} />
                  ))}
                </TableBody>
              </Table>
              <div className="px-6 py-3">
                <ListPagination
                  page={list.page}
                  totalPages={list.totalPages}
                  onPageChange={setPage}
                />
              </div>
            </>
          ))}
      </div>
    </div>
  );
}

function OpenOrderRow({ po, now }: { po: OpenPurchaseOrder; now: Date }) {
  const since = po.lastReceivedOn ?? po.raisedOn;
  return (
    <TableRow>
      <TableCell className="font-medium tabular-nums">
        {po.purchaseOrderNo}
      </TableCell>
      <TableCell>
        <Link
          to={paths.supplier({ supplierNo: po.supplierNo })}
          className="hover:underline"
        >
          {po.supplierName}
        </Link>
      </TableCell>
      <TableCell>
        {po.orderNo ? (
          <>
            {po.orderNo}
            {po.customerName && (
              <span className="text-muted-foreground">
                {" "}
                · {po.customerName}
              </span>
            )}
          </>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </TableCell>
      <TableCell className="tabular-nums">
        {formatDate(po.raisedOn)}
        {po.buyerName && (
          <div className="text-muted-foreground text-xs">by {po.buyerName}</div>
        )}
      </TableCell>
      <TableCell className="tabular-nums">
        {po.lastReceivedOn ? formatDate(po.lastReceivedOn) : null}
        {since && (
          <div
            className={cn(
              "text-xs",
              po.looksMissing
                ? "text-amber-600 dark:text-amber-500"
                : "text-muted-foreground",
            )}
          >
            {ageLabel(`${since}T00:00:00Z`, now)} ago
          </div>
        )}
        {po.looksMissing && (
          <Badge variant="warning" className="mt-1 font-normal">
            Invoice looks missing
          </Badge>
        )}
        {po.waitingPurchaseInvoice && (
          <Link
            to={paths.objectRecord({
              objectId: "finance_purchase_ledger.purchase_invoice",
              recordKey: po.waitingPurchaseInvoice.id,
            })}
            className="mt-1 block text-xs underline underline-offset-2"
          >
            Invoice waiting:{" "}
            {po.waitingPurchaseInvoice.supplierDocumentNo ?? "unnumbered"}
          </Link>
        )}
      </TableCell>
      <TableCell className="text-right font-medium tabular-nums">
        {formatMoney(po.netAmount)}
      </TableCell>
      <TableCell className="text-right">
        <Button asChild variant="outline" size="sm">
          <Link
            to={paths.purchaseOrderDetail({
              purchaseOrderNo: po.purchaseOrderNo,
            })}
          >
            View PO
          </Link>
        </Button>
      </TableCell>
    </TableRow>
  );
}
